/**
 * Payment methods. Add a new method by:
 *   1. Creating `src/sections/payment/providers/<id>.js` exporting
 *      async init(order) => { redirectUrl } | { ok: true }
 *   2. Adding an object to this array with a matching `provider` import.
 *
 * Each method can optionally declare:
 *   - `incompatibleDelivery: ['ukrposhta', ...]`  — delivery method IDs for
 *     which this payment is unavailable. The renderer disables the radio
 *     and auto-switches the selection off.
 *   - `notices: { '<deliveryId>': 'text' }`        — short helper text shown
 *     under the payment label per delivery method (fees, restrictions, …).
 *   - `cartNotice`  — highlighted note shown INSIDE this method's box while it
 *     is selected, when any cart line carries one of `flagValues` in the
 *     `lineField` that server/routes/cart.js puts on the line. Purely
 *     informational: it never disables the method or blocks checkout.
 *
 * Presentation (optional — methods without these keep the compact one-line
 * "mark + label — desc" layout):
 *   - `title`    customer-facing heading. Defaults to `label`.
 *   - `logos`    keys from src/sections/payment/logos.js, rendered as a row of
 *                acceptance chips under the title.
 *   - `footnote` { text, mark, suffix } small line under the logos, e.g.
 *                "Безпечна оплата через [plata] by mono".
 *
 * `label` is also sent to GA4 as `payment_type` (src/main.js add_payment_info),
 * so keep it STABLE — change `title` for display wording instead, otherwise
 * the payment report splits into two rows for the same method.
 */
export const PAYMENT_METHODS = [
  {
    id: 'plata',
    label: 'Plata by mono',          // analytics name (GA4 payment_type) — keep stable
    // Lead with what the customer recognises — card and wallet brands — and
    // demote the processor to a trust line underneath.
    title: 'Оплата карткою',
    logos: ['visa', 'mastercard', 'applepay', 'googlepay'],
    footnote: { text: 'Безпечна оплата через', mark: 'plata', suffix: 'by mono' },
    provider: () => import('../sections/payment/providers/plata.js'),
  },
  {
    id: 'cod',
    label: 'Післяплата',
    desc: '',
    mark: null,
    provider: () => import('../sections/payment/providers/cod.js'),
    // Meest does not offer cash on delivery for this merchant; Rozetka does.
    incompatibleDelivery: ['ukrposhta', 'meest'],
    // Some products are flagged in Shopify (product metafield
    // custom.np_cod_policy) as ones the manager may want prepaid before
    // shipping. `yes` and an empty value are the normal case and show nothing.
    cartNotice: {
      lineField: 'codPolicy',
      flagValues: ['no', 'prepay_10'],
      text: 'У кошику присутні товари на які менеджер може потребувати внести предоплату перед відправкою.',
      linkPrefix: 'Подробніше про політику доставки можете ',
      linkText: 'переглянути тут',
      linkUrl: 'https://wanmag-home.com/policies/shipping-policy',
      linkSuffix: '.',
    },
    notices: {
      novaposhta: 'Вартість послуги за тарифами Нової пошти: 2% вартості замовлення + 20 грн.',
      ukrposhta:  'Ця послуга наразі недоступна для доставки Укрпоштою.',
      meest:      'Ця послуга наразі недоступна для доставки Meest.',
    },
  },
];
